import { inject, Injectable, InjectionToken } from '@angular/core';
import { UserAuthService } from 'src/app/auth/services/user-auth.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

export const HELP_WIDGET_URL = new InjectionToken<string>('HELP_WIDGET_URL', {
  providedIn: 'root',
  factory: () => 'https://help.risevision.com/',
});

export const HELP_WIDGET_SCRIPT = new InjectionToken<string>(
  'HELP_WIDGET_SCRIPT',
  {
    providedIn: 'root',
    factory: () =>
      '!function(e,l,v,i,o,n){e[i]||(e[i]={}),e[i].account_id=n;var g,h;g=l.createElement(v),g.type="text/javascript",g.async=1,g.src=o+n,h=l.getElementsByTagName(v)[0],h.parentNode.insertBefore(g,h);e[i].q=[];e[i].on=function(z,y){e[i].q.push([z,y])}}(window,document,"script","_elev","https://cdn.elev.io/sdk/bootloader/v4/elevio-bootloader.js?cid=","5f2331387a97f");',
  },
);

interface ElevioApi {
  on(eventName: string, callback: (elevioApi?: ElevioApi) => void): void;
  setSettings(settings: { hideLauncher: boolean }): void;
  openHome(): void;
  openModule(moduleId: string): void;
  openArticle(articleId: string): void;
}

@Injectable({
  providedIn: 'root',
})
export class HelpWidgetService {
  private helpUrl = inject(HELP_WIDGET_URL);
  private helpWidgetScript = inject(HELP_WIDGET_SCRIPT);
  private userAuthService = inject(UserAuthService);
  private userStateService = inject(UserStateService);
  private broadcasterService = inject(BroadcasterService);

  private loaded = false;
  private widgetScriptLoaded = false;
  private hideLauncher = true;
  private authEventsInitialized = false;

  initialize(): void {
    if (!this.helpWidgetScript) {
      return;
    }

    this.initializeWidget();
    this.initializeAuthEvents();
  }

  initializeWidget(): void {
    if (this.loaded) {
      return;
    }

    const scriptElem = window.document.createElement('script');
    scriptElem.innerText = this.helpWidgetScript;

    window.document.body.appendChild(scriptElem);

    this.elevioApi?.on('load', (elevioApi?: ElevioApi) => {
      this.widgetScriptLoaded = true;
      (elevioApi || this.elevioApi)?.setSettings({
        hideLauncher: this.hideLauncher,
      });
    });

    this.loaded = true;

    this.elevioApi?.on('widget:closed', () => {
      const helpButton = window.document.getElementById('helpWidgetButton');

      if (helpButton) {
        setTimeout(() => {
          helpButton.focus();
        }, 500);
      }
    });

    this.elevioApi?.on('widget:opened', () => {
      const divElement = window.document.getElementById('_elev_io_placebo');
      const inputElement = divElement?.querySelector<HTMLInputElement>(
        'input[type="search"][name="search-input"]',
      );

      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
        }, 500);
      }
    });
  }

  showWidgetButton(): void {
    if (this.elevioApi && this.widgetScriptLoaded) {
      this.elevioApi.setSettings({
        hideLauncher: false,
      });
    } else {
      this.hideLauncher = false;
    }
  }

  hideWidgetButton(): void {
    if (this.elevioApi && this.widgetScriptLoaded) {
      this.elevioApi.setSettings({
        hideLauncher: true,
      });
    } else {
      this.hideLauncher = true;
    }
  }

  showHelpWidget(): void {
    if (this.elevioApi && this.widgetScriptLoaded) {
      this.elevioApi.openHome();
    } else {
      window.open(this.helpUrl, '_blank');
    }
  }

  showContactUs(): void {
    if (this.elevioApi && this.widgetScriptLoaded) {
      this.elevioApi.openHome();
      this.elevioApi.openModule('2');
    } else {
      window.open(this.helpUrl, '_blank');
    }
  }

  openArticle(articleId: string, helpCenterUrl: string): void {
    if (this.elevioApi && this.widgetScriptLoaded) {
      this.elevioApi.openArticle(articleId);
    } else {
      window.open(helpCenterUrl, '_blank');
    }
  }

  private initializeAuthEvents(): void {
    if (this.authEventsInitialized) {
      return;
    }

    this.authEventsInitialized = true;

    this.userAuthService
      .authenticate()
      .then(() => {
        if (!this.userStateService.isLoggedIn()) {
          this.showWidgetButton();
        }
      })
      .catch(() => {
        this.showWidgetButton();
      });

    this.broadcasterService.on('risevision.user.authorized', () => {
      this.hideWidgetButton();
    });

    this.broadcasterService.on('risevision.user.signedOut', () => {
      this.showWidgetButton();
    });
  }

  private get elevioApi(): ElevioApi | undefined {
    return (window as Window & { _elev?: ElevioApi })._elev;
  }
}
